<template>
  <div class="placement-test">
    <Loading v-if="loading" />
    <h1>{{ placementTest.name }} - {{ placementTest.level }}</h1>
    <div
      class="placementTestRow"
      v-for="({ id, question, answers, data }, index) in questions"
      :key="id"
      :class="{ missing: validated && !postData.questions[index].answer }"
      v-show="
        !submitted ||
          (Object.keys(testResults).length > 0 &&
            mistakeOnly &&
            !testResults.answers[index].is_correct)
      "
    >
      <div class="question">
        <div class="questionNumber">{{ index + 1 }}/</div>
        <div class="questionBody">
          <div
            class="sentence"
            v-for="(sentence, index) in parseSentence(question)"
            :key="`sentence-${index}`"
          >
            <i class="fas fa-user-circle" v-if="index % 2 === 0"></i>
            <i class="far fa-user-circle" v-else></i>
            <span style="padding-left: 10px">{{ sentence }}</span>
          </div>
        </div>
      </div>
      <template v-if="data && data['media']">
        <div
          v-for="(media, m_idx) in data['media']"
          :key="`media-${m_idx}`"
          class="questionMedia"
        >
          <template v-if="media.file.endsWith('mp3')">
            <span v-if="media.name">{{ media.name }}: </span>
            <AudioPlayer
              :sources="[$b2_tests_media + '/' + media.file]"
              ref="audioPlayers"
            />
          </template>
          <template v-else>
            <div
              class="imageViewLink"
              v-if="media.name"
              @click="showImagePopup(media.name, media.file)"
            >
              {{ $t("Show picture") }}: {{ media.name }}
            </div>
          </template>
        </div>
      </template>
      <div
        class="answer"
        v-for="(answer, idxAnswer) in answers"
        :key="`${idxAnswer}-${answer}`"
      >
        <div class="checkbox" @click="setAnswer(index, answer)">
          <i
            class="far fa-check-square"
            v-if="
              postData.questions[index] &&
                postData.questions[index].answer == answer
            "
          />
          <i class="far fa-square" v-else />
        </div>
        <div
          v-if="Object.keys(testResults).length > 0"
          class="answerOption"
          :class="{
            mistake:
              testResults.answers[index].answer_selected == answer &&
              !testResults.answers[index].is_correct,
            correct:
              testResults.answers[index].question.correct_answer == answer
          }"
        >
          {{ answer }}
        </div>
        <div v-else>
          {{ answer }}
        </div>
      </div>
    </div>
    <div class="stats-box-area">
      <template v-if="Object.keys(testResults).length > 0">
        <PlacementTestGrading @handleDoneTest="$emit('handleDoneTest')" />
      </template>
      <button class="btn" @click="grade" v-if="!submitted">
        {{ $t("grade test") }}
      </button>
      <button
        class="btn time-left"
        v-if="!submitted && this.countDown >= 0"
        :class="{ timesup: countDown == 0 }"
      >
        {{ $t("time left") }} {{ min }}:{{ sec }}
      </button>
    </div>

    <van-popup v-model="popupImgVisible">
      <van-image
        v-if="popupImgVisible"
        width="100%"
        height="auto"
        fit="contain"
        :src="popupImgSrc"
        alt="`Question associated image, ${popupImgLabel}`"
      />
    </van-popup>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import Loading from "@/components/Loading";
import PlacementTestGrading from "@/components/PlacementTestGrading";
import AudioPlayer from "@/components/AudioPlayer";

export default {
  name: "PlacementTest",
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  components: {
    PlacementTestGrading,
    Loading,
    AudioPlayer
  },
  computed: {
    ...mapState("placementTest", ["placementTest", "testResults", "loading"]),
    questions() {
      return this.placementTest?.questions;
    },
    min() {
      let mins = Math.floor(this.countDown / 60);
      return mins < 10 ? `0${mins}` : mins;
    },
    sec() {
      let seconds = this.countDown - Math.floor(this.countDown / 60) * 60;
      return seconds < 10 ? `0${seconds}` : seconds;
    }
  },
  data() {
    return {
      // 30 mins in secs (30 * 60), disables timer if < 0
      // change also in PlacementTests.vue, if u change value of 1800 to smth else
      countDown: 1800,
      submitted: false,
      validated: false,
      mistakeOnly: true,
      popupImgVisible: false,
      popupImgSrc: "",
      popupImgLabel: "",
      postData: {
        id: "",
        questions: []
      }
    };
  },
  watch: {
    testResults() {
      this.submitted = true;
    }
  },
  methods: {
    ...mapMutations("setting", ["SET_PAGE_TITLE", "SET_BACK_ROUTE"]),
    ...mapMutations("placementTest", [
      "SET_PLACEMENT_TEST",
      "SET_TEST_RESULTS",
      "RESET_TEST_RESULTS"
    ]),
    ...mapActions("placementTest", ["getPlacementTest", "gradePlacementTest"]),
    setAnswer(index, answer) {
      this.postData.questions[index].answer = answer;
    },
    showImagePopup(name, image) {
      this.popupImgSrc = this.$b2_tests_media + "/" + image;
      this.popupImgVisible = true;
    },
    grade() {
      this.validated = true;
      if (
        !this.submitted &&
        !this.postData.questions.some(({ answer }) => answer == "")
      ) {
        this.gradePlacementTest(this.postData);
      } else {
        this.$notify({
          type: "error",
          group: "alert",
          title: this.$t("Warning"),
          text: this.$t("Please answer every question!")
        });
      }
    },
    parseSentence(sentence) {
      if (sentence) {
        return sentence
          .trim()
          .split("- ")
          .filter(i => i != "");
      }
      return [];
    },
    countDownTimer() {
      if (this.countDown > 0 && !this.submitted) {
        setTimeout(() => {
          this.countDown -= 1;
          this.countDownTimer();
        }, 1000);
      }
      if (this.countDown == 0 && !this.submitted) {
        this.gradePlacementTest(this.postData);
      }
    }
  },
  created() {
    this.getPlacementTest(this.id).then(data => {
      this.postData.id = data.id;
      this.postData.questions = data.questions.map(({ id }) => ({
        id,
        answer: ""
      }));
      this.countDown = Number.isInteger(data?.time) ? data?.time : 1800;
      // if ("time" in data) {
      //   const jsCountDown = data.time;
      //   if (Number.isInteger(jsCountDown)) {
      //     this.countDown = data.time;
      //   }
      // }
      if (this.countDown > 0) {
        this.countDownTimer();
      }
      // else {
      //   this.countDown = -1;
      // }
    });
    this.SET_PAGE_TITLE("Placement Test");
  },
  destroyed() {
    this.SET_PLACEMENT_TEST([]);
    this.SET_TEST_RESULTS([]);
    this.SET_BACK_ROUTE({
      name: "Home"
    });
    this.RESET_TEST_RESULTS();
  }
};
</script>

<style lang="scss">
.placement-test {
  padding: 16px;
  // height: calc(100vh - 205px) !important;

  .placementTestRow {
    &:first-child {
      border-top: 1px solid #e3e3e3;
    }
    &.missing {
      border: 1px solid rgba(255, 0, 0, 0.6);
      border-bottom: none;
    }

    padding: 20px;
    border-bottom: 1px solid #e3e3e3;
    .question,
    .answer {
      padding: 10px;
    }
    .question {
      display: flex;
      .questionNumber {
        width: 30px;
        font-weight: bold;
      }
      .questionBody {
        .sentence {
          padding-bottom: 10px;
          display: flex;
          align-items: center;
        }
      }
    }
    .questionMedia {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      padding-bottom: 20px;
      > span {
        align-self: center;
        // line-height: 1.4;
        // padding-top: 8px;
        margin-bottom: 3px;
      }

      .imageViewLink {
        cursor: pointer;
        padding: 4px;
      }
    }
    .answer {
      padding-left: 40px;
      .checkbox {
        padding-left: 10px;
        padding-right: 10px;
      }
      display: flex;

      .answerOption {
        &.correct {
          border-bottom: 7px solid rgba(0, 128, 0, 0.5);
        }
        &.mistake {
          text-decoration: line-through solid red;
        }
      }
    }
  }

  .stats-box-area {
    position: absolute;
    justify-content: center;
    bottom: 0;
    background: white;
    width: 100%;
    height: 91px;
    left: 0px;
    border-top: 1px solid #e3e3e3;
    text-align: center;
    display: flex;
    justify-items: center;
    padding: 20px;
    z-index: 100;
  }
  .time-left {
    cursor: default;
    pointer-events: none;
    background: rgba(255, 0, 0, 0.6);
  }
  .timesup {
    background: rgba(255, 0, 0, 0.7);
    box-shadow: 0 0 10px rgba(255, 0, 0, 1);
  }
}
</style>
